import {
  BookedTravelerCollectionInt,
  CabinUpgradeAncillary,
  PremiereUpgradeInt,
  flightsNewInfoInt,
  iropEligibilityInt,
} from '@interfaces/*';
import { FARES_FAMILY } from '@app/enums';
import { Passenger } from '@models/passenger.model';
import { SegmentAWSInt } from '@app/interfaces/reservation-aws.interface';
import { CabinInfoList } from '@models/micro/response/flightDetails.model';

export class Segment {
  aircraftType: string;
  bookingClass: string;
  segmentStatusCode: string;
  arrivalAirport: string;
  arrivalCountry: string;
  arrivalCityName: string;
  arrivalDateTime: string;
  arrivalAirportRegion: string;
  arrivalTime: string;
  arrivalAirportTimeZoneId: string;
  departureAirport: string;
  departureCityName: string;
  departureDateTime: string;
  departureTime: string;
  departureCountry: string;
  estimatedArrivalTime: string;
  estimatedDepartureTime: string;
  fareFamily: FARES_FAMILY;
  flightNumber: string;
  flightsNewInfo: flightsNewInfoInt;
  flightStatus: string;
  iropEligibility: iropEligibilityInt;
  isCancelOag: boolean;
  isChangeOag: boolean;
  itineraryPartType: string;
  marketingCarrier: string;
  marketingFlightCode: string;
  operatingCarrier: string;
  operatingFlightCode: string;
  segmentCode: string;
  upgradingPassengers: Passenger[] = [];
  departureAirportTimeZoneId: string;
  departureTerminal: string;
  cabinInfo: CabinInfoList;
  windowSegmentStatus: string;
  cabinClass: string;
  farebasis: string;
  flightDuration: number;
  sequence: number;
  remainingSegmentTimeToCheckin: number;
  segmentEntity: string;
  segmentRegion: string;
  id: string;
  onUpgradeList: boolean;
  hiddenStop?: { departureDateTime?: string };

  constructor(segment?: SegmentAWSInt) {
    if (segment) {
      this.aircraftType = segment?.aircraftType;
      this.bookingClass = segment?.bookingClass;
      this.segmentStatusCode = segment?.segmentStatusCode;
      this.arrivalAirport = segment?.arrivalAirport;
      this.arrivalCountry = segment?.arrivalCountry;
      this.arrivalDateTime = segment?.arrivalDateTime;
      this.arrivalAirportRegion = segment?.arrivalAirportRegion;
      this.arrivalTime = this.getTime(segment?.arrivalDateTime);
      this.arrivalAirportTimeZoneId = segment?.arrivalAirportTimeZoneId;
      this.departureAirport = segment?.departureAirport;
      this.departureDateTime = segment?.departureDateTime;
      this.departureTime = this.getTime(segment?.departureDateTime);
      this.departureCountry = segment?.departureCountry;
      this.fareFamily = segment?.fareFamily;
      this.flightNumber = segment?.operatingCarrier + ' ' + segment?.operatingFlightCode;
      this.flightStatus = segment?.flightStatus;
      this.segmentCode = segment?.segmentCode;
      this.marketingCarrier = segment?.marketingCarrier;
      this.marketingFlightCode = segment?.marketingFlightCode;
      this.operatingCarrier = segment?.operatingCarrier;
      this.operatingFlightCode = segment?.operatingFlightCode;

      this.estimatedArrivalTime = segment?.estimatedArrivalTime;
      this.estimatedDepartureTime = segment?.estimatedDepartureTime;
      this.departureAirportTimeZoneId = segment.departureAirportTimeZoneId;

      this.flightsNewInfo = segment?.flightsNewInfo;
      this.iropEligibility = segment?.iropEligibility;
      this.isCancelOag = false;
      this.isChangeOag = false;
      this.bookingClass = segment?.bookingClass;
      this.departureTerminal = segment?.departureTerminal;
      this.arrivalCityName = '';
      this.departureCityName = '';
      this.farebasis = segment?.farebasis;
      this.windowSegmentStatus = segment.windowSegmentStatus;
      this.cabinClass = segment.cabinClass;
      this.farebasis = segment.farebasis;
      this.flightDuration = segment.flightDuration;
      this.sequence = segment.sequence;
      this.remainingSegmentTimeToCheckin = segment.remainingSegmentTimeToCheckin;
      this.segmentEntity = segment.segmentEntity;
      this.segmentRegion = segment.segmentRegion;
      this.sequence = segment.sequence;
      this.id = segment.id;
      this.segmentRegion = segment.segmentRegion;
      this.onUpgradeList = segment?.onUpgradeList;
      this.hiddenStop = segment?.hiddenStop;
    }
  }

  hasCustomersUpgradingPremiere() {
    return this.upgradingPassengers.length > 0;
  }

  removePassengerFromSegmentUpgrade(passengerToRemove) {
    const upgradingPassengers = [];
    passengerToRemove.upgradeSelected = false;
    for (let index = 0; index < this.upgradingPassengers.length; index++) {
      const passenger = this.upgradingPassengers[index];

      if (passenger.id != passengerToRemove.id) {
        upgradingPassengers.push(passenger);
      }
    }
    this.upgradingPassengers = upgradingPassengers;
  }

  getPremiereUpgradeOfferImage() {
    switch (this.aircraftType) {
      case '787':
      case '789':
        return 'images/dreamliner_large_banner2.png';
      case 'E70':
      case 'E7S':
      case 'E90':
      case 'E95':
      case 'E9S':
        return 'images/embraer_large_banner.png';
      default:
        return 'images/premiereCabinUpgrade.png';
    }
  }

  passengerIsPremiere(passengerToFind) {
    if (passengerToFind == null) {
      return false;
    }
    for (let index = 0; index < this.upgradingPassengers.length; index++) {
      const passenger = this.upgradingPassengers[index];
      if (passenger.id == passengerToFind.id) {
        return true;
      }
    }
    return false;
  }

  getParsedDepartureDateTime() {
    return this.parseApiDate(this.departureDateTime);
  }

  parseApiDate(apiDate) {
    try {
      const tokens = apiDate.split('T');
      const date = this.parseApiYearMonthDay(tokens[0]);
      this.parseApiTime(tokens[1], date);

      return date;
    } catch (e) {
      return null;
    }
  }

  parseApiYearMonthDay(yearMonthDay) {
    const tokens = yearMonthDay.split('-');
    return {
      year: tokens[0],
      month: tokens[1],
      day: tokens[2],
    };
  }

  parseApiTime(time, date) {
    const tokens = time.split(':');
    date.hour = tokens[0];
    date.minute = tokens[1];
    date.second = tokens[2];
  }

  private buildPremiereUpgrade(
    cart: BookedTravelerCollectionInt,
    segmentCode: string,
    isPremierAvailable
  ): PremiereUpgradeInt {
    const premiereUpgrade: PremiereUpgradeInt = {};
    const offer = this.findSegmentsOffer(cart, segmentCode);
    if (offer == null || offer.currency == null || !isPremierAvailable) {
      premiereUpgrade.available = false;
      premiereUpgrade.availableSeats = 0;
    } else {
      premiereUpgrade.available = true;
      premiereUpgrade.currency = offer.currency;
      premiereUpgrade.upgradeCostPerPax = offer.currency.total;
      premiereUpgrade.availableSeats = offer.seatsRemaining;
    }
    return premiereUpgrade;
  }

  private findSegmentsOffer(
    cart: BookedTravelerCollectionInt,
    segmentCode: string
  ): CabinUpgradeAncillary {
    if (cart == null || cart.upsellOffers == null || cart.upsellOffers._collection == null) {
      return null;
    }
    for (let index = 0; index < cart.upsellOffers._collection.length; index++) {
      const offer = cart.upsellOffers._collection[index];
      if (segmentCode == offer.segmentCode) {
        return offer;
      }
    }
    return null;
  }
  private getTime(dateTime) {
    let time = '';

    if (dateTime != null) {
      let timeIndex = dateTime.indexOf('T');
      if (timeIndex != -1) {
        timeIndex++;
      }

      time = dateTime.substr(timeIndex, 5);
    }

    return time;
  }

  public static buildFromSegmentBestMatchItinerary(segment: object) {
    const segment_ = new Segment();
    Object.assign(segment_, segment);
    return segment_;
  }
}
